import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import './PinScreen.css';

/* ═══════════════════════════════════════════════════════════════════════════
   PinScreen — TV pairing screen

   Displays a large 6-digit PIN code that the admin enters in the dashboard
   to link this TV to a specific showcase. Once paired, the token is stored
   in localStorage and the TV navigates to the showcase.

   Flow:
     1. Generate PIN via RPC → display it
     2. Poll every 3 seconds to check if admin has paired it
     3. When paired → receive showcase token → store → callback
   ═══════════════════════════════════════════════════════════════════════════ */

/** How often to poll for pairing status (ms) */
const POLL_INTERVAL_MS = 3_000;

/** PIN expiry time — matches DB constraint (10 minutes) */
const PIN_EXPIRY_MS = 10 * 60 * 1000;

interface PinScreenProps {
  /** Called when the PIN is successfully paired to a showcase */
  onPaired: (token: string) => void;
}

export default function PinScreen({ onPaired }: PinScreenProps) {
  const [pin, setPin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const generatePin = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    setPin(null);
    clearAllTimers();

    try {
      const { data, error: rpcError } = await supabase
        .rpc('generate_tv_pin');

      if (rpcError || !data) {
        setError('Could not generate a PIN code. Please check your connection.');
        setIsGenerating(false);
        return;
      }

      const pinCode = (data as { pin_code: string }).pin_code;
      const expiry = Date.now() + PIN_EXPIRY_MS;
      setPin(pinCode);
      setExpiresAt(expiry);
      setTimeLeft(PIN_EXPIRY_MS);
      setIsGenerating(false);

      // Start polling for pairing
      pollRef.current = setInterval(async () => {
        try {
          const { data: checkData } = await supabase
            .rpc('check_tv_pin', { lookup_pin: pinCode });

          if (checkData && (checkData as { token: string }).token) {
            const token = (checkData as { token: string }).token;
            clearAllTimers();
            // Store token for automatic reconnect on reboot
            localStorage.setItem('showcase-paired-token', token);
            onPaired(token);
          }
        } catch {
          // Silent — keep polling
        }
      }, POLL_INTERVAL_MS);

      // Start countdown timer
      timerRef.current = setInterval(() => {
        const remaining = expiry - Date.now();
        if (remaining <= 0) {
          // PIN expired — auto-regenerate
          clearAllTimers();
          generatePin();
        } else {
          setTimeLeft(remaining);
        }
      }, 1000);
    } catch {
      setError('Network error. Please check your connection.');
      setIsGenerating(false);
    }
  }, [clearAllTimers, onPaired]);

  // Generate PIN on mount
  useEffect(() => {
    generatePin();
    return clearAllTimers;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Format time remaining
  const minutes = Math.floor(timeLeft / 60_000);
  const seconds = Math.floor((timeLeft % 60_000) / 1000);
  const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Split PIN into individual digits for styling
  const digits = pin ? pin.split('') : [];

  return (
    <div className="pin-screen">
      <div className="pin-screen__content">
        <h1 className="pin-screen__title">RACEPROX</h1>
        <p className="pin-screen__subtitle">TV Showcase</p>

        <div className="pin-screen__card">
          {isGenerating ? (
            <div className="pin-screen__generating">
              <div className="pin-screen__spinner" />
              <p>Generating PIN…</p>
            </div>
          ) : error ? (
            <div className="pin-screen__error">
              <p>{error}</p>
              <button className="pin-screen__retry-btn" onClick={generatePin}>
                Try Again
              </button>
            </div>
          ) : (
            <>
              <p className="pin-screen__instruction">
                Enter this code in your admin panel
              </p>

              <div className="pin-screen__digits">
                {digits.map((d, i) => (
                  <div key={i} className="pin-screen__digit">
                    {d}
                  </div>
                ))}
              </div>

              <p className="pin-screen__timer">
                Expires in <span className="pin-screen__timer-value">{timeString}</span>
              </p>

              <div className="pin-screen__steps">
                <div className="pin-screen__step">
                  <span className="pin-screen__step-num">1</span>
                  <span>Open your showcase in the admin dashboard</span>
                </div>
                <div className="pin-screen__step">
                  <span className="pin-screen__step-num">2</span>
                  <span>Click "Connect TV" and enter this code</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
